import React, { useMemo } from 'react';
import useNow from '../hooks/useNow';
import {
  clamp, effectiveDurationSeconds, missionScore, formatAmount, formatCountdown, formatDuration,
  formatPercent, levelRange, parseServerTime,
} from '../utils/missionMath';
import {
  COIN_ICON, DNA_ICON, missionName, resolveAssetUrl, rewardIcon, rewardName,
} from '../utils/lairAssets';
import { ChanceIcon, ClockIcon, LockIcon, PowerIcon } from './LairIcons';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { msg, tr } from '../../../i18n/msg';
import { entityText } from '../../../i18n/entityText';

const DAY_MS = 24 * 3600 * 1000;

// v1.41.0 mission types: EXPEDITION (timed, as before) | BRAWLER | MATCH3 (interactive, v1.42.0) | SOLITAIRE (v1.43.0)
// TACTICS (goblins / orcs) removed in v1.43.0 — configs are deactivated on the server
export const MISSION_TYPES = {
  EXPEDITION: { icon: '🧭', label: msg('mission.type.expedition', 'Expedition') },
  BRAWLER: { icon: '💥', label: msg('mission.type.brawler', 'Brawler') },
  MATCH3: { icon: '💎', label: msg('mission.type.match3', 'Match-3') },
  SOLITAIRE: { icon: '🃏', label: msg('mission.type.solitaire', 'Solitaire') },
};

export const missionTypeOf = (m) => {
  const t = String(m?.missionType || m?.interactive || 'EXPEDITION').toUpperCase();
  return MISSION_TYPES[t] ? t : 'EXPEDITION';
};

export const isInteractive = (m) => missionTypeOf(m) !== 'EXPEDITION';

/** Route of the game page of an interactive run (null for expeditions). */
export function interactiveRunPath(run) {
  const t = missionTypeOf(run);
  if (t === 'BRAWLER') return `/lair/brawl/${run.id}`;
  if (t === 'MATCH3') return `/lair/match3/${run.id}`;
  if (t === 'SOLITAIRE') return `/lair/solitaire/${run.id}`;
  return null;
}

export function MissionTypeBadge({ mission }) {
  const { t } = useTranslation();
  const type = missionTypeOf(mission);
  const meta = MISSION_TYPES[type];
  return (
    <span className={`lair-type lair-type--${type.toLowerCase()}`}>{meta.icon} {tr(t, meta.label)}</span>
  );
}

const amountRange = (r) => {
  const min = Number(r.amountMin);
  const max = Number(r.amountMax);
  return min === max || !Number.isFinite(max) ? formatAmount(min) : `${formatAmount(min)}–${formatAmount(max)}`;
};

export function missionDurationSeconds(mission, speedBonus) {
  const eff = Number(mission?.effectiveDurationSeconds);
  return Number.isFinite(eff) && eff > 0 ? eff : effectiveDurationSeconds(mission?.durationMinutes, speedBonus);
}

/**
 * Why a mission can't be started right now (null = available).
 * kind: 'building' (not tappable) | 'heroes' | 'power' (tappable — the sheet explains).
 */
export function missionLock(mission, completeHeroes) {
  const t = i18n.t.bind(i18n);
  if (mission.unlocked === false) {
    const lvl = mission.unlockBuildingLevel ?? 1;
    return {
      kind: 'building',
      text: mission.unlockBuildingName
        ? t('mission.lock.requires', 'Requires «{{name}} Lv {{n}}»', {
          name: entityText(t, 'lair_building', mission.unlockBuildingId, 'name', mission.unlockBuildingName),
          n: lvl,
        })
        : t('mission.lock.locked', 'Locked'),
    };
  }
  if (completeHeroes.length === 0) return { kind: 'heroes', text: t('mission.lock.no_heroes', 'Complete a hero to start') };
  const best = Math.max(...completeHeroes.map((c) => missionScore(mission, c)));
  if (best < Number(mission.requiredPower || 0)) {
    return { kind: 'power', text: t('mission.lock.too_weak', 'Your heroes are too weak yet') };
  }
  return null;
}

export function RewardChip({ reward, amount, showDrop = false }) {
  const { t } = useTranslation();
  const icon = rewardIcon(reward);
  const drop = Number(reward.dropChance);
  return (
    <div className="lair-reward-chip" title={rewardName(reward, t)}>
      {icon ? <img src={icon} alt="" /> : <span className="lair-reward-chip__ph">?</span>}
      <span className="lair-reward-chip__amount">{amount}</span>
      {showDrop && Number.isFinite(drop) && drop < 1 && (
        <span className="lair-reward-chip__drop">{formatPercent(drop)}</span>
      )}
    </div>
  );
}

function RunCard({
  run, hero, serverNow, claiming, onClaim, onContinue,
}) {
  const { t } = useTranslation();
  const playPath = run.status === 'IN_PROGRESS' ? interactiveRunPath(run) : null;
  const start = parseServerTime(run.startedAt);
  const end = parseServerTime(run.endsAt);
  const remaining = Number.isFinite(end) ? end - serverNow : 0;
  const ready = run.ready || remaining <= 0;
  const span = end - start;
  const pct = ready ? 100 : span > 0 ? clamp(((serverNow - start) / span) * 100, 0, 100) : 0;

  return (
    <div className={`lair-run${ready ? ' lair-run--ready' : ''}`}>
      <div className="lair-run__thumb">
        <img src={resolveAssetUrl(run.missionImageUrl) || '/lair/patrol.svg'} alt="" />
        {hero?.headImageUrl && (
          <img className="lair-run__hero" src={resolveAssetUrl(hero.headImageUrl)} alt="" />
        )}
      </div>
      <div className="lair-run__body">
        <div className="lair-run__title">{missionName(run, t)}</div>
        <div className="lair-run__meta">
          {playPath && <MissionTypeBadge mission={run} />}
          <span><PowerIcon />{formatAmount(run.characterPower)}</span>
          {!playPath && <span><ChanceIcon />{formatPercent(run.successChance)}</span>}
        </div>
        {playPath ? (
          <button
            type="button"
            className="app-btn app-btn--blue app-btn--sm lair-run__claim"
            onClick={() => onContinue && onContinue(run, playPath)}
          >
            ▶ {t('mission.continue', 'Continue')}
          </button>
        ) : ready ? (
          <button
            type="button"
            className="app-btn app-btn--green app-btn--sm lair-run__claim"
            disabled={claiming}
            onClick={() => onClaim(run.id)}
          >
            {claiming ? t('lair.claiming', 'Claiming…') : t('common.claim', 'Claim')}
          </button>
        ) : (
          <div className="lair-progress">
            <div className="lair-progress__fill" style={{ width: `${pct}%` }} />
            <span className="lair-progress__text">{formatCountdown(remaining)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ClaimedRunRow({ run }) {
  const { t } = useTranslation();
  const rewards = run.rewards || [];
  return (
    <div className="lair-run-done">
      <img className="lair-run-done__img" src={resolveAssetUrl(run.missionImageUrl) || '/lair/patrol.svg'} alt="" />
      <div className="lair-run-done__body">
        <div className="lair-run-done__head">
          <span className="lair-run-done__name">{missionName(run, t)}</span>
          {Number(run.stars) > 0 && (
            <span className="lair-stars">{'★'.repeat(Math.min(3, Number(run.stars)))}</span>
          )}
          <span className={`lair-tag ${run.success ? 'lair-tag--ok' : 'lair-tag--fail'}`}>
            {run.success ? t('mission.tag.success', 'Success') : t('mission.tag.failed', 'Failed')}
          </span>
        </div>
        <div className="lair-run-done__rewards">
          {rewards.length === 0
            ? <span className="lair-muted">{t('mission.no_rewards', 'No rewards')}</span>
            : rewards.map((r, i) => (
              <RewardChip key={`${r.rewardType}-${r.resourceCode || r.moduleId || ''}-${i}`} reward={r} amount={`+${formatAmount(r.amount)}`} />
            ))}
        </div>
      </div>
    </div>
  );
}

/** Mission row (v1.47.0): picture, name, type and what it pays — coins by hero level + DNA. */
function MissionCard({ mission, bonuses, lock, onSelect }) {
  const { t } = useTranslation();
  const tappable = !lock || lock.kind !== 'building';
  const interactive = isInteractive(mission);
  const dna = Number(mission.dnaReward) || 0;
  const hasLevelCoins = Array.isArray(mission.coinsByLevel) && mission.coinsByLevel.length > 0;

  return (
    <div
      className={`lair-mission lair-mission--simple${lock ? ' lair-mission--locked' : ''}${lock?.kind === 'building' ? ' lair-mission--hard-lock' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => tappable && onSelect(mission)}
    >
      <div className="lair-mission__img">
        <img src={resolveAssetUrl(mission.imageUrl) || '/lair/patrol.svg'} alt="" />
      </div>
      <div className="lair-mission__body">
        <div className="lair-mission__head">
          <div className="lair-mission__name">{missionName(mission, t)}</div>
          <MissionTypeBadge mission={mission} />
        </div>
        <div className="lair-mission__rewards">
          {hasLevelCoins && (
            <div className="lair-reward-chip" title={t('mission.coins_hint', 'Coins, more for a higher hero level')}>
              <img src={COIN_ICON} alt="" />
              <span className="lair-reward-chip__amount">{levelRange(mission.coinsByLevel)}</span>
            </div>
          )}
          {dna > 0 && (
            <div className="lair-reward-chip" title={t('mission.dna_hint', 'DNA for a win')}>
              <img src={DNA_ICON} alt="" />
              <span className="lair-reward-chip__amount">{dna}</span>
            </div>
          )}
          {(mission.rewards || []).map((r, i) => (
            <RewardChip key={`${r.rewardType}-${r.resourceId || ''}-${i}`} reward={r} amount={amountRange(r)} showDrop />
          ))}
          {!interactive && (
            <span className="lair-stat lair-mission__time"><ClockIcon />{formatDuration(missionDurationSeconds(mission, bonuses?.missionSpeed))}</span>
          )}
        </div>
      </div>
      {lock && (
        <div className="lair-mission__lock">
          <LockIcon className="lair-glyph lair-glyph--lock" />
          <span>{lock.text}</span>
        </div>
      )}
    </div>
  );
}

export default function MissionsTab({
  data, loaded, error, offsetMs, claimingRunId, onSelectMission, onClaim, onRetry, onContinue,
}) {
  const { t } = useTranslation();
  const runs = useMemo(() => data?.runs || [], [data]);
  const characters = useMemo(() => data?.characters || [], [data]);
  const completeHeroes = useMemo(() => characters.filter((c) => c.complete), [characters]);
  const heroById = useMemo(() => Object.fromEntries(characters.map((c) => [c.id, c])), [characters]);

  const activeRuns = runs.filter((r) => r.status === 'IN_PROGRESS');
  const now = useNow(1000, activeRuns.length > 0);
  const serverNow = now + (offsetMs || 0);
  const claimedRuns = runs.filter((r) => {
    if (r.status !== 'CLAIMED') return false;
    const at = parseServerTime(r.claimedAt || r.endsAt);
    return !Number.isFinite(at) || serverNow - at < DAY_MS;
  });

  if (!loaded && !data) {
    return error
      ? <ErrorBox error={error} onRetry={onRetry} />
      : <div className="lair-loading"><div className="lair-spinner" /></div>;
  }

  const missions = data?.missions || [];

  return (
    <div className="lair-tab">
      {activeRuns.length > 0 && (
        <section className="lair-section">
          <h3 className="lair-section__title">{t('mission.section.active', 'Active')}</h3>
          <div className="lair-runs">
            {activeRuns.map((run) => (
              <RunCard
                key={run.id}
                run={run}
                hero={heroById[run.characterId]}
                serverNow={serverNow}
                claiming={claimingRunId === run.id}
                onClaim={onClaim}
                onContinue={onContinue}
              />
            ))}
          </div>
        </section>
      )}

      <section className="lair-section">
        <h3 className="lair-section__title">{t('mission.section.missions', 'Missions')}</h3>
        {missions.length === 0 ? (
          <div className="lair-empty">{t('mission.none', 'No missions available yet')}</div>
        ) : (
          <div className="lair-missions">
            {missions.map((m) => (
              <MissionCard
                key={m.id}
                mission={m}
                bonuses={data?.bonuses}
                lock={missionLock(m, completeHeroes)}
                onSelect={onSelectMission}
              />
            ))}
          </div>
        )}
      </section>

      {claimedRuns.length > 0 && (
        <section className="lair-section">
          <h3 className="lair-section__title">{t('mission.section.completed', 'Completed')}</h3>
          <div className="lair-runs">
            {claimedRuns.map((run) => <ClaimedRunRow key={run.id} run={run} />)}
          </div>
        </section>
      )}
    </div>
  );
}

export function ErrorBox({ error, onRetry }) {
  const { t } = useTranslation();
  return (
    <div className="lair-empty">
      <div>{typeof error === 'string' ? error : t('common.failed_to_load', 'Failed to load')}</div>
      {onRetry && (
        <button type="button" className="app-btn app-btn--purple app-btn--sm" onClick={onRetry}>
          {t('common.retry', 'Retry')}
        </button>
      )}
    </div>
  );
}
